"use client";

import { motion } from "framer-motion";
import { MapPin, Navigation, Clock, Mountain } from "lucide-react";
import { SITE } from "@/lib/constants";

const NEARBY = [
  { name: "Nandi Hills Summit", distance: "8 km", time: "15 min" },
  { name: "Bhoga Nandeeshwara Temple", distance: "5 km", time: "10 min" },
  { name: "Nandi Hill Trek Start", distance: "3 km", time: "5 min" },
  { name: "Kempegowda International Airport", distance: "35 km", time: "45 min" },
  { name: "Bangalore City Center", distance: "60 km", time: "1.5 hrs" },
];

export function LocationSection() {
  return (
    <section id="location" className="section-padding bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
            Find Us
          </p>
          <h2 className="mt-3 font-playfair text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            Location
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Map Placeholder */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-card"
          >
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3876.5!2d${SITE.coordinates.lng}!3d${SITE.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDIyJzEyLjciTiA3N8KwNDEnMDAuNiJF!5e0!3m2!1sen!2sin!4v1`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mountain Nest Villa Location"
              className="absolute inset-0"
            />
            <a
              href={`https://www.google.com/maps?q=${SITE.coordinates.lat},${SITE.coordinates.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 left-4 z-10 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg transition-colors hover:bg-primary/90"
            >
              <Navigation className="size-4" />
              Get Directions
            </a>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col justify-center"
          >
            <div className="flex items-center gap-3 rounded-xl bg-card/60 p-4 mb-6 border border-border">
              <Navigation className="size-5 text-primary shrink-0" />
              <div>
                <p className="font-semibold text-foreground">
                  60 km from Bangalore
                </p>
                <p className="text-sm text-muted-foreground">
                  Approximately 1.5 hours drive via NH44
                </p>
              </div>
            </div>

            <h3 className="font-playfair text-xl font-semibold text-foreground">
              Nearby Attractions
            </h3>
            <div className="mt-4 space-y-3">
              {NEARBY.map((place, i) => (
                <motion.div
                  key={place.name}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-center justify-between rounded-lg border border-border bg-card/40 px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <Mountain className="size-4 text-primary shrink-0" />
                    <span className="text-sm font-medium text-foreground">
                      {place.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>{place.distance}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="size-3" />
                      {place.time}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
